import React, { useState } from 'react';
import {
  Box,
  Button,
  Alert,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArchiveIcon from '@mui/icons-material/Archive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import type { IncidentStatus, IncidentDetail, DispatchIncidentInput } from '../../types/canvision.types';
import { ROUTE_SCHEDULER } from 'constants/routerPath';
import DispatchDialog from './DispatchDialog';
import DismissConfirmDialog from '../DismissConfirmDialog';

interface IncidentActionsProps {
  incident: IncidentDetail;
  onStatusChange: (status: IncidentStatus, notes?: string) => Promise<void>;
  onViewPatrol?: (patrolId: number) => void;
}

function IncidentActions({ incident, onStatusChange, onViewPatrol }: IncidentActionsProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);

  const handleStatusAction = async (status: IncidentStatus, notes?: string) => {
    try {
      setLoading(true);
      setError(null);
      await onStatusChange(status, notes);
    } catch (err: any) {
      setError(err.message || `Failed to update incident to ${status}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDismissConfirm = async (notes?: string) => {
    try {
      setLoading(true);
      setError(null);
      await onStatusChange('dismissed', notes);
    } catch (err: any) {
      setError(err.message || 'Failed to dismiss incident');
    } finally {
      setLoading(false);
      setDismissDialogOpen(false);
    }
  };

  const handleDispatch = () => {
    const params = new URLSearchParams();
    params.set('action', 'newPatrol');
    if (incident.customer_name) params.set('customerName', incident.customer_name);
    if (incident.address_title) params.set('addressName', incident.address_title);
    navigate(`${ROUTE_SCHEDULER}?${params.toString()}`);
  };

  const getActions = () => {
    switch (incident.status) {
      case 'new':
        return (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={() => handleStatusAction('reviewing')}
            disabled={loading}
          >
            Start Review
          </Button>
        );
      case 'reviewing':
        return (
          <>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SendIcon />}
              onClick={handleDispatch}
              disabled={loading}
            >
              Dispatch Guard
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={() => setDismissDialogOpen(true)}
              disabled={loading}
            >
              Dismiss
            </Button>
          </>
        );
      case 'dispatched':
        return (
          <>
            {incident.patrol_id && (
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => onViewPatrol?.(incident.patrol_id!)}
              >
                View Patrol
              </Button>
            )}
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleStatusAction('resolved')}
              disabled={loading}
            >
              Resolve
            </Button>
          </>
        );
      case 'resolved':
        return (
          <Button
            variant="outlined"
            startIcon={<ArchiveIcon />}
            onClick={() => handleStatusAction('closed')}
            disabled={loading}
          >
            Close
          </Button>
        );
      case 'closed':
      case 'dismissed':
        return null; // Terminal states — no actions
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {getActions()}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <DismissConfirmDialog
        open={dismissDialogOpen}
        onClose={() => setDismissDialogOpen(false)}
        onConfirm={handleDismissConfirm}
        incidentTitle={incident.title}
        severity={incident.severity}
        loading={loading}
        showNotes
      />
    </Box>
  );
}

export default IncidentActions;
